import { Button, Form, Input, PageHeader, Image, message, Upload, Space, Row, Col } from 'antd';
import ImgCrop from 'antd-img-crop';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { useQueryClient } from 'react-query';
import { UpdateUserRequest } from 'services/api/client/src';
import errorHandler from 'utils/errorHandler';
import { useUpdateUser, useUserInfo, useUploadProfilePicture } from 'services/api/useSettings';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { AsYouType, CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { PhoneCodeSelect } from 'components/GSelect';
import React, { ChangeEvent, useState, useEffect } from 'react';
import useModalVisibility from 'hooks/useModalVisibility';
import { PhoneCode } from 'utils';
import ResetPassword from 'components/ResetPassword';

import styles from './index.module.less';

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

const Profile: React.FC = () => {
  const [form] = Form.useForm();
  const [profileForm] = Form.useForm();
  const resetPasswordModal = useModalVisibility(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [preview, setPreview] = useState(false);
  const [, setPhoneCode] = useState('+1');
  const [phoneISO, setPhoneISO] = useState<CountryCode | string | undefined>('US');

  const queryClient = useQueryClient();
  const updateUser = useUpdateUser(queryClient);
  const { data: loggedInUserData } = useUserInfo();
  const uploadProfile = useUploadProfilePicture(queryClient);

  useEffect(() => {
    form.setFieldsValue({
      name: loggedInUserData?.user?.name,
      email: loggedInUserData?.user?.email,
      contactNumber: loggedInUserData?.user?.phone,
    });
  }, [form, loggedInUserData]);

  const onFinish = async (values: any) => {
    try {
      const reqData: UpdateUserRequest = {
        phone: values.contactNumber,
        name: values.name,
      };
      await updateUser.mutateAsync({
        params: reqData,
      });
      message.success('Successfully updated data');
    } catch (error: any) {
      message.error(errorHandler(error));
    }
  };

  const onResetPassword = () => {
    resetPasswordModal.show();
  };

  const onResetClick = async (value: any) => {
    try {
      const reqData: UpdateUserRequest = {
        password: value.newPassword,
      };
      await updateUser.mutateAsync({
        params: reqData,
      });
      message.success('Successfully reset password');
    } catch (error: any) {
      message.error(errorHandler(error));
    }
  };

  // upload profile picture
  const onUploadChange = (info: UploadChangeParam<UploadFile<any>>) => {
    const { file } = info;
    const { status } = file;

    if (status === 'done') {
      message.success(`${info.file.name} file uploaded successfully.`);
      if (file?.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        file.url = blobUrl;
      }
    } else if (status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };
  const onRemove = () => {
    setFileList([]);
  };
  const onUpload: UploadProps['customRequest'] = async ({
    file,
    onProgress,
    onSuccess,
    onError,
  }) => {
    const success = message.loading('Uploading image...', 0);
    const blobUrl = URL.createObjectURL(file as RcFile);
    try {
      onProgress?.({ percent: 0 });
      setTimeout(() => onProgress?.({ percent: 50 }), 1000);
      onProgress?.({ percent: 100 });
      const fileRes = await uploadProfile.mutateAsync({ picture: file });
      setFileList([{ uid: fileRes?.id || '1', name: 'profile.png', status: 'done', url: blobUrl }]);
      onSuccess?.(file);
      success();
      onRemove();
    } catch (error) {
      success();
      onError?.({
        name: 'upload-error',
        message: errorHandler(error),
      });
      onRemove();
    }
  };
  const onPreview = () => {
    setPreview(true);
  };

  return (
    <>
      {resetPasswordModal && resetPasswordModal.visible && (
        <ResetPassword modal={resetPasswordModal} onResetClick={onResetClick} />
      )}
      <PageHeader title="My Profile" className={styles['page-header']} />
      <div className={styles.baseView}>
        <div className={styles.left}>
          <Form
            {...formItemLayout}
            form={form}
            name="register"
            onFinish={onFinish}
            initialValues={{
              name: loggedInUserData?.user?.name,
              email: loggedInUserData?.user?.email,
              contactNumber: loggedInUserData?.user?.phone,
            }}
            scrollToFirstError
            layout="vertical"
          >
            <Form.Item label="Email address" required>
              <Row gutter={8}>
                <Col xs={24} sm={24} md={20} lg={20} xl={18}>
                  <Form.Item
                    name="email"
                    rules={[
                      {
                        type: 'email',
                        message: 'The input is not valid Email!',
                      },
                      {
                        required: true,
                        message: 'Please input your Email!',
                      },
                    ]}
                  >
                    <Input allowClear placeholder="Enter Email" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={4} lg={4} xl={6}>
                  <Button type="link" onClick={onResetPassword} style={{ padding: '0px' }}>
                    Reset Password
                  </Button>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item name="name" label="Name">
              <Input allowClear placeholder="Enter Name" />
            </Form.Item>

            {/* <Form.Item name="country" label="Country / Region">
              <Select placeholder="Country / Region" />
            </Form.Item>
            <Form.Item name="province" label="Province / City">
              <div style={{ display: 'flex', gap: '8px' }}>
                <Select placeholder="Province" />
                <Select placeholder="City" disabled />
              </div>
            </Form.Item> */}

            <Form.Item
              name="contactNumber"
              label="Contact Number"
              rules={[
                {
                  message: 'Please input a valid phone number',
                  validator: (_, value) => {
                    if (!value?.trim?.()) {
                      return Promise.resolve();
                    }
                    if (isValidPhoneNumber(value, phoneISO as CountryCode)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Please input a valid phone number'));
                  },
                },
              ]}
            >
              <Input
                style={{ width: '100%' }}
                placeholder="Contact Number"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const countryCode = phoneISO || 'US';
                  const value = new AsYouType(countryCode as CountryCode).input(e.target.value);
                  form?.setFieldsValue({
                    contactNumber: value,
                  });
                }}
                addonBefore={
                  <PhoneCodeSelect
                    onChange={(val, option) => {
                      // @ts-ignore
                      const item: PhoneCode = option?.itemProps;
                      setPhoneCode(`+${String(item?.code || '').replace('+', '')}`);
                      setPhoneISO(item.iso);
                    }}
                  />
                }
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                style={{
                  color: '#0a5f7a',
                  borderColor: '#bcd530',
                  background: '#bcd530',
                  textShadow: '0px 0px',
                  boxShadow: '0px 0px',
                }}
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
        <Form form={profileForm} name="avatar" layout="vertical" className={styles.right}>
          <Form.Item label={<div className={styles.avatar_title}>Avatar</div>}>
            <Space direction="vertical" size="middle" style={{ alignItems: 'center' }}>
              <Image
                width={144}
                height={144}
                src={`${loggedInUserData?.user?.profilePictureUrl}?q=${performance.now()}`}
                fallback="data:image/png;base64,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"
                preview={{
                  visible: preview,
                  src: `${loggedInUserData?.user?.profilePictureUrl}?q=${performance.now()}`,
                  onVisibleChange: (value) => {
                    setPreview(value);
                  },
                }}
              />
              <ImgCrop rotate shape="round">
                <Upload
                  fileList={fileList}
                  onPreview={onPreview}
                  onRemove={onRemove}
                  customRequest={onUpload}
                  onChange={onUploadChange}
                  maxCount={1}
                  style={{ paddingLeft: '24px' }}
                >
                  {fileList.length > 0 ? null : (
                    <Button shape="round" type="primary" ghost icon={<UploadOutlined />}>
                      Click to Upload
                    </Button>
                  )}
                </Upload>
              </ImgCrop>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default Profile;
